import { useState } from 'react'
import { Download, FileText, Loader2, Share } from 'lucide-react'
import { toast } from 'react-toastify'
import { cn } from 'components/UI/cn'
import {
  openSyndigoSpecsheet,
  bulkDownloadSpecsheetsOptimized,
  shareSpecsheet,
  type AsyncBulkDownloadResponse,
  type BulkDownloadResult,
  type VariantCatalog,
} from 'services/products'
import { downloadFile } from 'utils/csvDownloader'

interface MobileVariantCardProps {
  variant: VariantCatalog
}

/** True when a variant has a Syndigo-backed spec sheet to view/share/download. */
export function hasSpecsheet(
  variant: Pick<VariantCatalog, 'syndigo_product'>
): boolean {
  return variant.syndigo_product != null
}

/** Pull the ready blob + filename out of a bulk-download response, or null when it's the async/polling response (sheet not ready yet). */
export function extractDownloadBlob(
  response: BulkDownloadResult | AsyncBulkDownloadResponse
): { blob: Blob; filename: string } | null {
  if ('blob' in response) {
    return { blob: response.blob, filename: response.filename }
  }
  return null
}

/** Open a variant's spec sheet in a new tab, toasting on failure. */
export async function viewSpecsheet(variantId: number): Promise<void> {
  try {
    await openSyndigoSpecsheet(variantId)
  } catch (err) {
    console.error('Error opening spec sheet:', err)
    toast.error('Error loading spec sheet. Please try again later.')
  }
}

/** Download a variant's spec sheet PDF; returns true when a file was saved. Toasts on a not-ready response or a download error. */
export async function downloadSpecsheet(variantId: number): Promise<boolean> {
  try {
    const response = await bulkDownloadSpecsheetsOptimized([variantId])
    const ready = extractDownloadBlob(response)
    if (ready) {
      downloadFile(ready.blob, ready.filename)
      return true
    }
    toast.error('Spec sheet is not ready yet. Please try again.')
  } catch (err) {
    console.error('Error downloading spec sheet:', err)
    toast.error('Error downloading spec sheet. Please try again later.')
  }
  return false
}

/** Share a variant's spec sheet via the OS share sheet, falling back to a download on browsers without Web Share file support. */
export async function shareSpecsheetWithFallback(
  variant: VariantCatalog
): Promise<void> {
  let result: Awaited<ReturnType<typeof shareSpecsheet>>
  try {
    result = await shareSpecsheet(variant)
  } catch (err) {
    console.error('Error sharing spec sheet:', err)
    toast.error('Error sharing spec sheet. Please try again later.')
    return
  }
  if (result === 'unsupported') {
    // Browsers without Web Share file support (desktop, some Android
    // WebViews) fall back to a plain download, which owns its own error
    // messaging so a download failure here isn't mislabeled as a share error.
    const downloaded = await downloadSpecsheet(variant.id)
    if (downloaded) {
      toast.info('Sharing is not supported here. Downloading instead.')
    }
  }
}

/** Variant card for the mobile products surface; spec-sheet actions render only for Syndigo-backed variants (FIR-7497). */
export function MobileVariantCard({ variant }: MobileVariantCardProps) {
  const [isViewing, setIsViewing] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const specsheetAvailable = hasSpecsheet(variant)

  const handleView = async () => {
    if (isViewing) return
    setIsViewing(true)
    try {
      await viewSpecsheet(variant.id)
    } finally {
      setIsViewing(false)
    }
  }

  const handleDownload = async () => {
    if (isDownloading) return
    setIsDownloading(true)
    try {
      await downloadSpecsheet(variant.id)
    } finally {
      setIsDownloading(false)
    }
  }

  const handleShare = async () => {
    if (isSharing) return
    setIsSharing(true)
    try {
      await shareSpecsheetWithFallback(variant)
    } finally {
      setIsSharing(false)
    }
  }

  return (
    <div className="rounded-lg border border-line bg-surface p-3 shadow-sm">
      <div className="text-sm font-semibold text-fg">{variant.name}</div>

      {variant.brand_name && (
        <div className="mt-0.5 text-xs text-fg-secondary">
          {variant.brand_name}
        </div>
      )}

      <div className="mt-2 space-y-0.5 text-xs text-fg-secondary">
        {variant.sku && <div>SKU: {variant.sku}</div>}
        {variant.gtin && <div>GTIN/UPC: {variant.gtin}</div>}
        {variant.format_pack_size && (
          <div>Format: {variant.format_pack_size}</div>
        )}
      </div>

      {specsheetAvailable ? (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={handleView}
            disabled={isViewing}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-md',
              'bg-brand px-3 py-2 text-xs font-medium text-white',
              'disabled:opacity-60'
            )}
          >
            {isViewing ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <FileText size={14} />
            )}
            View spec sheet
          </button>
          <button
            type="button"
            onClick={handleShare}
            disabled={isSharing}
            aria-label="Share spec sheet"
            className={cn(
              'flex items-center justify-center gap-1.5 rounded-md',
              'border border-line px-3 py-2 text-xs font-medium text-fg',
              'disabled:opacity-60'
            )}
          >
            {isSharing ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Share size={14} />
            )}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
            aria-label="Download spec sheet"
            className={cn(
              'flex items-center justify-center gap-1.5 rounded-md',
              'border border-line px-3 py-2 text-xs font-medium text-fg',
              'disabled:opacity-60'
            )}
          >
            {isDownloading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
          </button>
        </div>
      ) : (
        <div className="mt-3 text-xs italic text-fg-secondary">
          No spec sheet available
        </div>
      )}
    </div>
  )
}
